'use client';

import React, { useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { ShieldCheck, RefreshCw, CheckCircle2, XCircle, Eye, EyeOff, Key, Zap, Clock, AlertTriangle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface CredentialEntry {
  label: string;
  masked: string;
  set: boolean;
}

interface TestResult {
  scope: string;
  status: 'ok' | 'error';
  detail: string;
}

interface CredentialsResponse {
  credentials: Record<string, CredentialEntry>;
  tests: {
    instagram: TestResult[];
    facebook: TestResult[];
    meta: TestResult[];
  };
  testedAt: string;
}

function StatusIcon({ status }: { status: 'ok' | 'error' }) {
  return status === 'ok'
    ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
    : <XCircle size={14} className="text-red-400 shrink-0" />;
}

function ScopeBadge({ status }: { status: 'ok' | 'error' }) {
  return (
    <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${status === 'ok' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
      {status === 'ok' ? 'PASS' : 'FAIL'}
    </span>
  );
}

function PermissionPanel({
  title,
  icon: Icon,
  color,
  results,
  loading,
}: {
  title: string;
  icon: React.ElementType;
  color: string;
  results: TestResult[];
  loading: boolean;
}) {
  const passCount = results.filter((r) => r.status === 'ok').length;
  const total = results.length;
  const allPass = total > 0 && passCount === total;
  const allFail = total > 0 && passCount === 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between pb-4 border-b border-border mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
            <Icon size={15} style={{ color }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {!loading && total > 0 && (
              <p className="text-xs text-muted-foreground">{passCount}/{total} checks passed</p>
            )}
          </div>
        </div>
        {!loading && total > 0 && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${allPass ? 'bg-emerald-500/15 text-emerald-400' : allFail ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
            {allPass ? '✓ All Clear' : allFail ? '✗ Failed' : '⚠ Partial'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-secondary animate-pulse" />
          ))}
        </div>
      ) : results.length === 0 ? (
        <p className="text-xs text-muted-foreground">No results yet. Run the test.</p>
      ) : (
        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-lg ${r.status === 'ok' ? 'bg-emerald-500/5 border border-emerald-500/15' : 'bg-red-500/5 border border-red-500/15'}`}>
              <StatusIcon status={r.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-foreground">{r.scope}</span>
                  <ScopeBadge status={r.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate" title={r.detail}>{r.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CredentialsPage() {
  const [data, setData] = useState<CredentialsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFull, setShowFull] = useState<Record<string, boolean>>({});
  const [hasRun, setHasRun] = useState(false);

  const runTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/meta/credentials-test');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: CredentialsResponse = await res.json();
      setData(json);
      setHasRun(true);
    } catch (e: unknown) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleShow = (key: string) => setShowFull((prev) => ({ ...prev, [key]: !prev[key] }));

  const credentialOrder = [
    'igBusinessAccountId',
    'igAccessToken',
    'fbPageId',
    'fbPageAccessToken',
    'metaBusinessId',
    'metaAppId',
    'metaAppSecret',
    'metaId',
  ];

  const totalPass = data
    ? [...data.tests.instagram, ...data.tests.facebook, ...data.tests.meta].filter((r) => r.status === 'ok').length
    : 0;
  const totalTests = data
    ? data.tests.instagram.length + data.tests.facebook.length + data.tests.meta.length
    : 0;

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#833ab420', border: '1px solid #833ab440' }}>
              <ShieldCheck size={18} style={{ color: '#c13584' }} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Credentials &amp; Permissions</h1>
              <p className="text-xs text-muted-foreground font-mono">Live token validation · IG · FB · Meta</p>
            </div>
          </div>
          <button
            onClick={runTest}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Testing…' : hasRun ? 'Re-run Tests' : 'Run Permission Tests'}
          </button>
        </div>

        {/* Summary bar (after run) */}
        {hasRun && data && (
          <div className="rounded-xl border border-border bg-card px-5 py-3 flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-primary" />
              <span className="text-sm font-semibold text-foreground">{totalPass}/{totalTests} checks passed</span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock size={12} />
              Tested at {new Date(data.testedAt).toLocaleTimeString()}
            </div>
            {totalPass < totalTests && (
              <>
                <div className="w-px h-4 bg-border" />
                <div className="flex items-center gap-2 text-xs text-amber-400">
                  <AlertTriangle size={12} />
                  {totalTests - totalPass} issue{totalTests - totalPass !== 1 ? 's' : ''} found
                </div>
              </>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Credentials Cards */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-3 pb-4 border-b border-border mb-5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Key size={15} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Wired Credentials</h2>
              <p className="text-xs text-muted-foreground">IDs and tokens from environment variables (masked)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {credentialOrder.map((key) => {
              // Static fallback from env (shown before test run)
              const staticMap: Record<string, { label: string; masked: string; set: boolean }> = {
                igBusinessAccountId: { label: 'Instagram Business Account ID', masked: '1784••••5856463', set: true },
                igAccessToken: { label: 'Instagram Access Token', masked: '(set — masked)', set: true },
                fbPageId: { label: 'Facebook Page ID', masked: '1239••••9284431', set: true },
                fbPageAccessToken: { label: 'FB Page Access Token', masked: '(set — masked)', set: true },
                metaBusinessId: { label: 'Meta Business ID', masked: '1346••••3701878', set: true },
                metaAppId: { label: 'Meta App ID', masked: '2803••••9275351', set: true },
                metaAppSecret: { label: 'Meta App Secret', masked: '1a01••••••••••••••••••••201c5f', set: true },
                metaId: { label: 'Meta App / User ID', masked: '(derived from token — see Meta Identity test)', set: true },
              };

              const cred = data?.credentials[key] ?? staticMap[key];
              if (!cred) return null;

              return (
                <div key={key} className="flex items-center gap-4 px-4 py-3 rounded-lg bg-secondary border border-border">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{cred.label}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-foreground truncate">
                        {showFull[key] ? cred.masked : cred.masked}
                      </span>
                      {cred.set ? (
                        <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 shrink-0">SET</span>
                      ) : (
                        <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 shrink-0">MISSING</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleShow(key)}
                    className="p-2 rounded-lg bg-card border border-border text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    title={showFull[key] ? 'Hide' : 'Show (still masked for security)'}
                  >
                    {showFull[key] ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Values are always masked for security. To update, edit your <code className="font-mono bg-card px-1 rounded">.env</code> file directly.
          </p>
        </div>

        {/* Permission Test Panels */}
        <div className="grid grid-cols-3 gap-5">
          <PermissionPanel
            title="Instagram Permissions"
            icon={() => (
              <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            )}
            color="#e1306c"
            results={data?.tests.instagram ?? []}
            loading={loading}
          />
          <PermissionPanel
            title="Facebook Permissions"
            icon={() => (
              <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            )}
            color="#1877f2"
            results={data?.tests.facebook ?? []}
            loading={loading}
          />
          <PermissionPanel
            title="Meta Account Permissions"
            icon={ShieldCheck}
            color="#833ab4"
            results={data?.tests.meta ?? []}
            loading={loading}
          />
        </div>

        {!hasRun && !loading && (
          <div className="rounded-xl border border-dashed border-border bg-card/50 py-10 flex flex-col items-center gap-3 text-center">
            <ShieldCheck size={32} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Click <strong className="text-foreground">Run Permission Tests</strong> to validate all IG, FB, and Meta scopes live.</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
